import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  Optional,
  TemplateRef,
  inject,
} from '@angular/core';
import { SearchInstancesManagerService, SearchResultsPaginationComponent } from '@anecdotes/design-system/data-manipulation';
import { delayedPromise, toDictionary } from '@anecdotes/utils';
import { BehaviorSubject, combineLatest, Observable, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface ItemContext {
  item: any;
  index: number;
  previousItem?: any;
  nextItem?: any;
}

@Component({
  selector: 'app-base-renderer',
  template: '',
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export abstract class BaseRenderer implements OnInit {
  protected readonly destroyRef = inject(DestroyRef);

  private readyState = new BehaviorSubject(false);
  private currentSearchScopeKey: string;
  private renderedItemsHash: string;
  private rendererItemsDictionary: { [id: string]: any };

  protected get isReady$(): Observable<boolean> {
    return this.readyState.pipe(filter((isReady) => isReady));
  }

  @Input() idSelector: (item) => string;
  @Input() buildRenderingItemsCallback: (items: any[]) => any[];
  @Input() itemAddingStream: Observable<any>;
  @Input() allItemsStream: Observable<any[]>;
  @Input() filteredItemsStream: Observable<any[]>;
  @Input() scrollToNewlyAddedItem: boolean;
  @Input() scrollToFocusedItem: boolean;

  @ContentChild('itemTemplate', { static: true }) itemTemplate: TemplateRef<any>;

  renderedItems: any[];

  renderedIds: string[];

  displayedItemIds: Set<string>;

  get isEmpty(): boolean {
    if (this.allItems && this.filteredItems) {
      return !this.allItems.length || !this.filteredItems.length;
    }

    return false;
  }

  trackBy: (index: number, item: any) => string | number = this.itemTrackBy.bind(this);

  protected allItemsDictionary: { [key: string]: any };
  protected allItems: any[];
  protected filteredItems: any[];

  constructor(
    @Optional() private searchInstancesManagerService: SearchInstancesManagerService,
    protected elementRef: ElementRef<HTMLElement>,
    protected cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentSearchScopeKey = this.searchInstancesManagerService?.getSearchScopeKey(this.elementRef.nativeElement);

    combineLatest([this.allItemsStream, this.filteredItemsStream])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([allItems, filteredItems]) => {
        this.handleFilteredItems(filteredItems);
        this.handleAllItemsChange(allItems);
        this.renderItems();
        this.readyState.next(true);
        this.cd.detectChanges();
      });

    if (this.currentSearchScopeKey) {
      this.searchInstancesManagerService.getSearchResultsPaginator(this.currentSearchScopeKey)
        .pipe(
          filter(Boolean),
          switchMap((searchPaginator: SearchResultsPaginationComponent) => {
            return searchPaginator.dataFocusChange
              .pipe(
                tap(() => {
                  if (this.scrollToFocusedItem) {
                    this.scrollInto(searchPaginator.currentOverlapIndexEntry.row);
                  }
                })
              );
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    this.itemAddingStream
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newItem) => this.handleAddingNewItem(newItem));
  }

  createItemTemplateContext(item: any, index: number, previousItem?: any, nextItem?: any): ItemContext {
    return {
      item,
      index,
      ...(previousItem && { previousItem }),
      ...(nextItem && { nextItem }),
    };
  }

  getRenderedItemById(id: string): any {
    return this.rendererItemsDictionary && this.rendererItemsDictionary[id] ? this.rendererItemsDictionary[id] : null;
  }

  selectId(item: any): string {
    if (this.idSelector) {
      return this.idSelector(item);
    }

    return item.id;
  }

  scrollInto(item: any): Promise<void> {
    return this.scrollToId(this.selectId(item));
  }

  abstract scrollToId(id: string): Promise<void>;

  protected abstract getItemsForRendering(): any[];

  private buildRenderedItems(): any[] {
    const itemsForRendering = this.getItemsForRendering() || [];

    if (this.buildRenderingItemsCallback) {
      return this.buildRenderingItemsCallback(itemsForRendering);
    }

    return itemsForRendering;
  }

  private async handleAddingNewItem(newItem: any): Promise<any> {
    this.filteredItems = [...this.filteredItems, newItem];
    this.renderItems();

    if (this.scrollToNewlyAddedItem) {
      await delayedPromise(500);
      this.scrollInto(newItem);
    }
  }

  private handleAllItemsChange(allItems: any[]): void {
    if (allItems) {
      this.allItems = allItems;
      this.allItemsDictionary = toDictionary(allItems, (item) => this.selectId(item) as string);

      if (this.filteredItems) {
        this.filteredItems = this.filteredItems.reduce((result: Array<any>, item) => {
          const itemFromDictionary = this.allItemsDictionary[this.selectId(item)];

          if (itemFromDictionary) {
            result.push(itemFromDictionary);
          }

          return result;
        }, []);
      }
    }
  }

  private handleFilteredItems(filteredItems: any[]): void {
    this.filteredItems = filteredItems;
  }

  private renderItems(): void {
    let displayedItemIdsArray: string[] = [];

    if (this.filteredItems) {
      displayedItemIdsArray = this.filteredItems.map((item) => this.selectId(item));
    }

    this.displayedItemIds = new Set(displayedItemIdsArray);
    const newRenderedItems = this.buildRenderedItems() || [];
    const newRenderedItemIds = newRenderedItems.map((item) => this.selectId(item));
    const newRenderedItemsHash = newRenderedItemIds.join('_');
    this.rendererItemsDictionary = toDictionary(newRenderedItems, (item) => this.selectId(item));

    if (this.renderedItemsHash !== newRenderedItemsHash) {
      this.renderedItemsHash = newRenderedItemsHash;
      this.renderedItems = newRenderedItems;
      this.renderedIds = newRenderedItemIds;
    }

    this.cd.detectChanges();
  }

  private itemTrackBy(index: number, itemId: any): any {
    return itemId || index;
  }
}
